import { useEffect, useState } from "react";
import { Loader2, School, FolderOpen, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { listClassrooms, organizeLecture } from "@/lib/api";
import { toast } from "sonner";

/**
 * Small, reusable dialog to file an existing lecture into a Class → Chapter
 * (or move it back out to My Library). Owner-only endpoint — the button that
 * opens this should already be gated on `lecture.owned`.
 */
export const MoveLectureDialog = ({ open, onOpenChange, lecture, onMoved }) => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [classroomId, setClassroomId] = useState("");
  const [chapterLabel, setChapterLabel] = useState("");

  useEffect(() => {
    if (!open) return;
    setClassroomId(lecture?.classroom_id || "");
    setChapterLabel(lecture?.chapter_label || "");
    listClassrooms().then(setClasses).catch(() => setClasses([]));
  }, [open, lecture?.id, lecture?.classroom_id, lecture?.chapter_label]);

  const save = async () => {
    if (!lecture?.id) return;
    setLoading(true);
    try {
      await organizeLecture(lecture.id, {
        classroom_id: classroomId || "",
        chapter_label: classroomId ? (chapterLabel || "Uploaded") : "",
      });
      toast.success(classroomId ? "Filed into your class library" : "Moved back to My Library");
      onOpenChange(false);
      if (onMoved) onMoved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not move this lecture.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="move-lecture-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Move this lecture</DialogTitle>
          <DialogDescription>
            File "{lecture?.title || "this lecture"}" into a class you teach — students still see nothing until you post it as an assignment.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5">
              <School className="h-3.5 w-3.5" /> Destination
            </label>
            <select
              data-testid="move-class-select"
              value={classroomId}
              onChange={(e) => setClassroomId(e.target.value)}
              className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/60 transition cursor-pointer"
            >
              <option value="">My Library (personal, no class)</option>
              {classes.map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.subject ? ` · ${c.subject}` : ""}</option>
              ))}
            </select>
            {classes.length === 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                You don't have any classes yet — visit the Instructor tab to create one.
              </p>
            )}
          </div>

          {classroomId && (
            <div data-testid="move-chapter-block">
              <label className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground flex items-center gap-1.5">
                <FolderOpen className="h-3.5 w-3.5" /> Chapter label
              </label>
              <input
                data-testid="move-chapter-input"
                value={chapterLabel}
                onChange={(e) => setChapterLabel(e.target.value)}
                placeholder="e.g. Chp 46 dental impressions"
                className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-ring/60 transition"
              />
              <p className="mt-1.5 text-[11px] text-muted-foreground">Files under this chapter inside the class library. You can drag it later.</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full border border-border px-5 py-2.5 text-sm font-semibold hover:border-primary/50 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="move-confirm-btn"
            onClick={save}
            disabled={loading}
            className="rounded-full bg-accent text-accent-foreground px-5 py-2.5 text-sm font-semibold hover:opacity-90 transition disabled:opacity-50 inline-flex items-center gap-2"
          >
            {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Moving…</> : <>Move <ArrowRight className="h-4 w-4" /></>}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
